import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { catchError, exhaustMap, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { concat, defer, EMPTY, from, Observable, of, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { StorageService } from '../../../core/storage/storage.service';
import { TranslateService } from '../../../core/translate/translate.service';
import { CookieService } from 'ngx-cookie-service';
import * as Reducers from '../../core/reducers';
import * as AuthReducers from '../reducers';
import { TagEnum } from '../../../settings/models/tag.enum';
import { JwtService } from '../../../core/services/jwt.service';
import { User } from '../../../auth/model/user';
import { HydratedUser, SessionCredentials, SessionResponse } from '../../../auth/model/session-response';
import { UrlHelper } from '../../../shared/helpers/url.helper';
import { AuthenticationService } from '../../../auth/services/authentication.service';
import { TokenRefreshTimerService } from '../../../auth/services/token-refresh-timer.service';
import { AnalyticsService } from '../../../core/services/analytics.service';
import { SessionCleanupService } from '../../../core/services/session-cleanup.service';
import { SessionCoordinatorService } from '../../../auth/services/session-coordinator.service';
import { AuthActions, AuthApiActions, SigninPageActions } from '../actions';

@Injectable()
export class AuthEffects {

  /** Restore persisted credentials once on startup; guards also request this if needed. */
  restoreOnStartup$ = createEffect(() => defer(() => {
    const credentials = this._storage.get('currentUser') as SessionCredentials | null;
    return credentials?.accessToken
      ? of(SigninPageActions.autoLogin({ url: this._router.url }))
      : EMPTY;
  }));

  signin$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SigninPageActions.signin),
      switchMap(action => {
        const generation = this._sessionCoordinator.begin();
        this._sessionCleanupService.end();
        return this._authenticationService.login(
          action.credentials.username,
          action.credentials.password,
          action.credentials.language
        ).pipe(
          switchMap(credentials => {
            const minimal = this._withResolvedExpiration(credentials);
            this._persistCredentials(minimal);
            return this._hydrateSession(minimal, action.url, generation, true);
          }),
          catchError(error => of(AuthApiActions.loginFailure({ error })))
        );
      })
    )
  );

  autoLogin$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SigninPageActions.autoLogin),
      exhaustMap(action => this._store$.select(AuthReducers.getSessionStatus).pipe(
        take(1),
        switchMap(status => status === 'ready' ? EMPTY : this._restoreSession(action.url))
      ))
    )
  );

  loginSuccess$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthApiActions.loginSuccess),
      tap(action => {
        const user = { ...action.user, expirationTokenDate: this._resolveExpiration(action.user) };
        this._persistCredentials(user);
        this._analyticsService.identityChanged('user_signed_in');
        if (document.getElementById('login')) {
          document.getElementById('login').setAttribute('id', 'body');
        }
        // navigateByUrl, not navigate([url]): the latter encodes a query string into the path.
        this._router.navigateByUrl(UrlHelper.getDefaultUrl(user, action.url));

        this._cookieService.delete('accessToken');
        if (user.expirationTokenDate) {
          this._tokenRefreshTimerService.startTimer(user.expirationTokenDate);
        }
      })
    ),
    { dispatch: false }
  );

  loginRedirect$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthApiActions.loginRedirect),
      tap(action => this._router.navigate(['/signin'], action.url ? { queryParams: { returnUrl: action.url } } : {}))
    ),
    { dispatch: false }
  );

  logout$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthActions.logout),
      // Capture the redirect tag before ending the session clears settings.
      switchMap(() => this._store$.select(Reducers.getTag, TagEnum.RedirectUrl.name.toUpperCase()).pipe(
        take(1),
        tap(redirectUrlTag => {
          this._logoutRedirectUrl = redirectUrlTag ? redirectUrlTag.value.replace(/"/g, '') : null;
        }),
        switchMap(() => this._authenticationService.logout().pipe(
          map(() => AuthApiActions.logoutSuccess()),
          catchError(() => of(AuthApiActions.logoutFailure({ error: null })))
        ))
      ))
    )
  );

  logoutSuccess$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthApiActions.logoutSuccess, AuthApiActions.logoutFailure),
      switchMap(() => {
        const currentUser = this._storage.get('currentUser');
        const redirectUrl = this._logoutRedirectUrl;
        this._logoutRedirectUrl = null;
        this._storage.remove('currentUser');
        this._sessionCoordinator.invalidate();
        this._analyticsService.identityChanged('user_signed_out');
        this._tokenRefreshTimerService.stopTimer();
        this._translateService.use('pl');
        this._sessionCleanupService.end();
        if (currentUser && redirectUrl) {
          window.location.href = redirectUrl;
          return EMPTY;
        }
        return of(AuthApiActions.loginRedirect({ url: null }));
      })
    )
  );

  updateToken$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthActions.updateToken),
      switchMap(action => {
        const stored = this._storage.get('currentUser') as SessionCredentials | null;
        if (!stored?.accessToken) {
          return EMPTY;
        }

        const credentials = this._withResolvedExpiration({ ...stored, accessToken: action.token });
        this._persistCredentials(credentials);
        if (credentials.expirationTokenDate) {
          this._tokenRefreshTimerService.startTimer(credentials.expirationTokenDate);
        }
        const generation = this._sessionCoordinator.begin();
        return this._store$.select(AuthReducers.getSession).pipe(
          take(1),
          switchMap(previous => this._authenticationService.getSessionOnce(credentials.accessToken).pipe(
            switchMap(session => this._sessionRefreshSuccess(credentials, session, previous, generation)),
            catchError(error => this._sessionFailure(error, null, generation))
          ))
        );
      })
    )
  );

  /** A visible tab refreshes its display session every minute and immediately after reconnect. */
  pollSession$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthApiActions.loginSuccess),
      switchMap(() => this._sessionCoordinator.pollTriggers().pipe(
        takeUntil(this._actions$.pipe(ofType(
          AuthApiActions.logoutSuccess,
          AuthApiActions.logoutFailure,
          SigninPageActions.autoLogout
        ))),
        exhaustMap(() => this._store$.select(AuthReducers.getUser).pipe(
          take(1),
          switchMap(user => {
            if (!user?.accessToken) {
              return EMPTY;
            }
            const generation = this._sessionCoordinator.currentGeneration();
            return this._store$.select(AuthReducers.getSession).pipe(
              take(1),
              switchMap(previous => this._authenticationService.getSessionOnce(user.accessToken).pipe(
                switchMap(session => this._sessionRefreshSuccess(user, session, previous, generation)),
                catchError(error => this._sessionFailure(error, null, generation))
              ))
            );
          })
        ))
      ))
    )
  );

  autoLogout$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SigninPageActions.autoLogout),
      tap(() => {
        this._sessionCoordinator.invalidate();
        this._storage.remove('currentUser');
        this._analyticsService.identityChanged('user_signed_out');
        this._tokenRefreshTimerService.stopTimer();
        this._sessionCleanupService.end();
      })
    ),
    { dispatch: false }
  );

  setUser$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthActions.setUser),
      switchMap(action => {
        const generation = this._sessionCoordinator.begin();
        this._sessionCleanupService.end();
        const existing = this._storage.get('currentUser') as SessionCredentials | null;
        const credentials = this._withResolvedExpiration({
          username: existing?.username,
          language: action.language,
          accessToken: action.token,
        });
        this._persistCredentials(credentials);
        this._translateService.use(action.language);
        return this._hydrateSession(credentials, null, generation);
      })
    )
  );

  changeUserLanguage$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthActions.changeUserLanguage),
      tap(action => {
        const user = this._storage.get('currentUser') as SessionCredentials | null;
        if (user && user.language !== action.language) {
          this._persistCredentials({ ...user, language: action.language });
          this._translateService.use(action.language);
          this._router.navigateByUrl('/settings/account');
        }
      })
    ),
    { dispatch: false }
  );

  forgotPassword$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthActions.forgotPassword),
      switchMap(action => this._authenticationService.forgotPassword({ userName: action.userName }).pipe(
        map(data => AuthApiActions.forgotPasswordSuccess({ data })),
        catchError(error => of(AuthApiActions.forgotPasswordFailure({ error })))
      ))
    )
  );

  resetPassword$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthActions.resetPassword),
      switchMap(action => this._authenticationService.resetPassword(action.data).pipe(
        map(data => AuthApiActions.resetPasswordSuccess({ data })),
        catchError(error => of(AuthApiActions.resetPasswordFailure({ error })))
      ))
    )
  );

  constructor(
    private _actions$: Actions,
    private _store$: Store<any>,
    private _authenticationService: AuthenticationService,
    private _router: Router,
    private _storage: StorageService,
    private _translateService: TranslateService,
    private _cookieService: CookieService,
    private _jwtService: JwtService,
    private _tokenRefreshTimerService: TokenRefreshTimerService,
    private _analyticsService: AnalyticsService,
    private _sessionCleanupService: SessionCleanupService,
    private _sessionCoordinator: SessionCoordinatorService,
  ) { }

  private _logoutRedirectUrl: string | null = null;

  private _restoreSession(url: string): Observable<Action> {
    const stored = this._storage.get('currentUser') as SessionCredentials | null;
    if (!stored?.accessToken) {
      return concat(
        of(SigninPageActions.autoLogout()),
        of(AuthApiActions.loginRedirect({ url }))
      );
    }

    const generation = this._sessionCoordinator.begin();
    const refresh$: Observable<SessionCredentials> = this._isTokenExpired(stored)
      ? this._authenticationService.refreshOnce(stored.accessToken).pipe(
        switchMap(response => response?.data?.accessToken
          ? of({ ...stored, accessToken: response.data.accessToken, expirationTokenDate: response.data.expirationTokenDate })
          : throwError(() => ({ status: 401 })))
      )
      : of(stored);

    return refresh$.pipe(
      switchMap(credentials => {
        const minimal = this._withResolvedExpiration(credentials);
        this._persistCredentials(minimal);
        return this._hydrateSession(minimal, url, generation);
      }),
      catchError(error => this._sessionFailure(error, url, generation))
    );
  }

  private _hydrateSession(
    credentials: SessionCredentials,
    url: string | null,
    generation: number,
    loginAttempt = false
  ): Observable<Action> {
    return this._authenticationService.getSessionOnce(credentials.accessToken).pipe(
      switchMap(session => {
        if (!this._sessionCoordinator.isCurrent(generation)) {
          return EMPTY;
        }
        this._sessionCoordinator.markSuccess(generation);
        const user = this._toHydratedUser(credentials, session);
        return of(AuthApiActions.loginSuccess({ user, url }));
      }),
      catchError(error => this._sessionFailure(error, url, generation, loginAttempt))
    );
  }

  private _sessionRefreshSuccess(
    credentials: SessionCredentials,
    session: SessionResponse,
    previous: SessionResponse | null,
    generation: number
  ): Observable<Action> {
    if (!this._sessionCoordinator.isCurrent(generation)) {
      return EMPTY;
    }
    const succeededAt = Date.now();
    this._sessionCoordinator.markSuccess(generation, succeededAt);
    const scopeChanged = !!previous && (
      previous.userAccountId !== session.userAccountId ||
      previous.clientId !== session.clientId ||
      previous.unitScopeHash !== session.unitScopeHash
    );
    if (scopeChanged) {
      this._sessionCleanupService.end();
    }
    return of(AuthApiActions.sessionRefreshSuccess({
      user: this._toHydratedUser(credentials, session),
      session,
      scopeChanged,
      succeededAt,
    }));
  }

  private _sessionFailure(error: any, url: string | null, generation: number, loginAttempt = false): Observable<Action> {
    if (!this._sessionCoordinator.isCurrent(generation)) {
      return EMPTY;
    }
    if (this._isUnauthorized(error)) {
      this._sessionCoordinator.invalidate();
      return concat(
        of(SigninPageActions.autoLogout()),
        of(AuthApiActions.loginRedirect({ url }))
      );
    }

    const failedAt = Date.now();
    const actions: Action[] = [AuthApiActions.sessionRefreshFailure({ failedAt, status: error?.status ?? 0 })];
    if (loginAttempt) {
      actions.push(AuthApiActions.loginFailure({ error }));
    }
    return from(actions);
  }

  private _toHydratedUser(credentials: SessionCredentials, session: SessionResponse): HydratedUser {
    return {
      ...credentials,
      username: session.userAccountLogin || credentials.username,
      userAccountId: session.userAccountId,
      clientId: session.clientId,
      clientName: session.clientName,
      modules: session.modules as User['modules'],
      privileges: session.privileges,
      isAdministrator: session.isAdministrator,
      unitScopeHash: session.unitScopeHash,
    } as HydratedUser;
  }

  private _withResolvedExpiration(credentials: SessionCredentials): SessionCredentials {
    return {
      accessToken: credentials.accessToken,
      expirationTokenDate: this._resolveExpiration(credentials),
      language: credentials.language,
      ...(credentials.username ? { username: credentials.username } : {}),
    };
  }

  private _persistCredentials(credentials: SessionCredentials | User): void {
    const user = credentials as SessionCredentials;
    const minimal: SessionCredentials = {
      accessToken: user.accessToken,
      expirationTokenDate: this._resolveExpiration(user),
      language: user.language,
      ...(user.username ? { username: user.username } : {}),
    };
    this._storage.set('currentUser', JSON.stringify(minimal));
  }

  private _resolveExpiration(user: Pick<SessionCredentials, 'accessToken' | 'expirationTokenDate'>): string | undefined {
    return user.expirationTokenDate ?? this._jwtService.getExpirationDate(user.accessToken) ?? undefined;
  }

  private _isTokenExpired(user: SessionCredentials): boolean {
    if (user.expirationTokenDate) {
      return new Date(user.expirationTokenDate) <= new Date();
    }
    try {
      const expiration = this._jwtService.getExpirationDate(user.accessToken);
      return expiration ? new Date(expiration) <= new Date() : false;
    } catch {
      return true;
    }
  }

  private _isUnauthorized(error: any): boolean {
    return error?.status === 401 || error?.status === 403;
  }
}
